import React, { useContext, useEffect } from 'react';
import { DatePicker, Form, Input, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import HolidaysContext from '@totem/components/holidays/HolidaysContext';
import { HolidayPayload } from '@totem/components/holidays/types';

type FormValues = {
  name: string;
  date: Dayjs;
};

const HolidaysFormModal = () => {
  const { modalOpen, closeModal, upsertHoliday, editingHoliday, saving } =
    useContext(HolidaysContext);
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (modalOpen) {
      if (editingHoliday) {
        form.setFieldsValue({
          name: editingHoliday.name,
          date: dayjs(editingHoliday.date),
        });
      } else {
        form.resetFields();
      }
    }
  }, [modalOpen, editingHoliday, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: HolidayPayload = {
      id: editingHoliday?.id,
      name: values.name.trim(),
      date: values.date.toISOString(),
    };
    await upsertHoliday(payload);
  };

  const title = editingHoliday ? 'Edit Holiday' : 'Add Holiday';

  return (
    <Modal
      open={modalOpen}
      onOk={handleSubmit}
      okText={editingHoliday ? 'Save Changes' : 'Add Holiday'}
      confirmLoading={saving}
      onCancel={closeModal}
      title={title}
      destroyOnHidden
      maskClosable={false}
    >
      <Form layout="vertical" form={form} name="holidayForm">
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: 'Please enter a holiday name' },
            { max: 100, message: 'Name should be under 100 characters' },
            {
              validator: (_, value) =>
                value && value.trim().length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error('Name cannot be blank')),
            },
          ]}
        >
          <Input placeholder="Holiday name" />
        </Form.Item>
        <Form.Item
          label="Date"
          name="date"
          rules={[{ required: true, message: 'Please choose a date' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default HolidaysFormModal;
